import React, { RefObject } from 'react';
import noop from 'lodash/noop';
import { cloneDeep } from 'lodash';
import { useDispatch } from 'react-redux';
import { setLines } from '../../actions/threadsUiSlice';

export type RegisteredElementType = 'MODULE' | 'MODIFIER' | 'THREAD';
export type ConnectedPointPosition = 'top' | 'bottom' | 'left' | 'right';

export type RegisteredElement = {
  connectedPointPosition: ConnectedPointPosition;
  element: HTMLDivElement;
  id: string;
  meta: { moduleId: string } | null;
  type: RegisteredElementType;
};

export type ConnectionPoint = {
  id: string;
};

export type Line = {
  source: ConnectionPoint;
  destination: ConnectionPoint;
};

export const ConnectorsProviderContext = React.createContext({
  name: '',
  elementsRef: {} as RefObject<Record<string, RegisteredElement>>,
  lines: {} as Record<string, Line>,
  registerElement: noop as (props: RegisteredElement) => void,
  unregisterElement: noop as (id: string) => void,
  registerLine: noop as (id: string, params: Line) => void,
  unRegisterLine: noop as (id: string) => void,
});

type ConnectorsProviderProps = {
  name: string;
  children: React.ReactNode;
};

const ConnectorsProvider = ({ name, children }: ConnectorsProviderProps) => {
  const dispatch = useDispatch();

  const elementsRef = React.useRef<Record<string, RegisteredElement>>({});
  const linesRef = React.useRef<Record<string, Line>>({});

  const handleRegisterElement = (props: RegisteredElement) => {
    const cloned = cloneDeep(elementsRef.current);
    cloned[props.id] = props;
    elementsRef.current = cloned;
  };

  const handleUnregisterElement = (id: string) => {
    const cloned = cloneDeep(elementsRef.current);
    delete cloned[id];
    elementsRef.current = cloned;
  };

  const handleRegisterLine = (id: string, { source, destination }: Line) => {
    const cloned = cloneDeep(linesRef.current);
    cloned[id] = { source, destination };
    linesRef.current = cloned;
    dispatch(setLines({ [name]: cloned }));
  };

  const handleUnRegisterLine = (id: string) => {
    const cloned = cloneDeep(linesRef.current);
    delete cloned[id];
    linesRef.current = cloned;
    dispatch(setLines({ [name]: cloned }));
  };

  return (
    <ConnectorsProviderContext.Provider
      value={{
        name,
        elementsRef,
        registerElement: handleRegisterElement,
        registerLine: handleRegisterLine,
        lines: linesRef.current,
        unregisterElement: handleUnregisterElement,
        unRegisterLine: handleUnRegisterLine,
      }}
    >
      {children}
    </ConnectorsProviderContext.Provider>
  );
};

export default ConnectorsProvider;
